import React from "react";
import styles from "./OrderSummary.module.css";

export interface OrderSummaryProduct {
  title?: string;
  /**
   * Optional variant label rendered as a muted sublabel beneath the title
   * (e.g. "Ice", "Powder"). Use this to disambiguate lines that share the
   * same product title — typically quantity-tier splits where one product
   * resolves to multiple variant lines in the cart.
   */
  variantTitle?: string;
  price?: string; // in cents as string, e.g. "2500" = $25.00
  /** Pre-discount price in cents as string. When set, renders crossed-out above the discounted price. */
  originalPrice?: string;
  image?: string;
  quantity: number;
  onQuantityChange?: (val: number) => void;
  maxQuantity?: number;
  onRemove?: () => void;
}

export interface OrderSummaryProps {
  products: OrderSummaryProduct[];
  currency: string;

  taxEnabled?: boolean;
  taxRate?: number;
  taxName?: string;
  taxIncludedInPrice?: boolean;
  shippingCost?: number;

  /**
   * Extra subtotal contribution from cart lines that should NOT render
   * as visible rows (e.g. 1-tick offer_embed selections, where the UX
   * pattern is "affect the total only, no separate row"). Already in
   * shop currency as a plain number.
   */
  hiddenItemsSubtotal?: number;

  title?: string;
  subtotalLabel?: string;
  shippingLabel?: string;
  totalLabel?: string;
  showTaxNotice?: boolean;
  /** Savings amount in shop currency (already × quantity). Renders a green discount row when > 0. */
  discountAmount?: number;
  /** Label for the discount row. Defaults to "Descuento". */
  discountLabel?: string;

  borderColor?: string;
  backgroundColor?: string;
  borderWidth?: number;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function parsePrice(priceInCents: string | undefined): number {
  if (!priceInCents) return 0;
  const parsed = Number(priceInCents);
  return isNaN(parsed) ? 0 : parsed / 100;
}

const PlaceholderIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  products,
  currency,
  taxEnabled,
  taxRate,
  taxName,
  taxIncludedInPrice,
  shippingCost,
  hiddenItemsSubtotal,
  title = "Resumen del pedido",
  subtotalLabel = "Subtotal",
  shippingLabel = "Envío",
  totalLabel = "Total",
  showTaxNotice,
  discountAmount,
  discountLabel = "Descuento",
  borderColor,
  backgroundColor,
  borderWidth,
}) => {
  const fmt = (amount: number) => formatCurrency(amount, currency);

  const subtotal =
    products.reduce(
      (sum, p) => sum + parsePrice(p.originalPrice ?? p.price) * p.quantity,
      0
    ) + (hiddenItemsSubtotal ?? 0);
  const discount = discountAmount ?? 0;
  const discountedSubtotal = subtotal - discount;
  const shipping = shippingCost ?? 0;

  let taxAmount = 0;
  if (taxEnabled && taxRate) {
    taxAmount = taxIncludedInPrice
      ? discountedSubtotal - discountedSubtotal / (1 + taxRate / 100)
      : (discountedSubtotal * taxRate) / 100;
  }
  const total =
    discountedSubtotal + shipping + (taxIncludedInPrice ? 0 : taxAmount);

  return (
    <div
      className={styles.wrapper}
      style={
        {
          "--order-summary-border-color": borderColor,
          "--order-summary-bg-color": backgroundColor,
          "--order-summary-border-width": borderWidth
            ? `${borderWidth}px`
            : undefined,
        } as React.CSSProperties
      }
    >
      <p className={styles.sectionTitle}>{title}</p>
      <div className={styles.container} role="region" aria-label={title}>
        {products.map((product, index) => {
          const interactive = !!product.onQuantityChange;
          const unitPrice = parsePrice(product.price);

          return (
            <div key={index} className={styles.productCard}>
              {product.onRemove && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={product.onRemove}
                  aria-label="Quitar del pedido"
                >
                  ×
                </button>
              )}
              <div className={styles.productImageWrapper}>
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.title || "Product"}
                    className={styles.productImage}
                  />
                ) : (
                  <div
                    className={styles.productImagePlaceholder}
                    role="presentation"
                  >
                    <PlaceholderIcon />
                  </div>
                )}
                <span className={styles.quantityBadge}>{product.quantity}</span>
              </div>

              <div className={styles.productInfo}>
                <p className={styles.productTitle}>
                  {product.title || "Product"}
                </p>
                {product.variantTitle && (
                  <p className={styles.productVariant}>
                    {product.variantTitle}
                  </p>
                )}
                {product.originalPrice && (
                  <p className={styles.originalPrice}>
                    {fmt(parsePrice(product.originalPrice) * product.quantity)}
                  </p>
                )}
                <p className={styles.productPrice}>
                  {fmt(unitPrice * product.quantity)}
                </p>
                <div className={styles.quantityControls}>
                  <button
                    type="button"
                    className={styles.quantityButton}
                    disabled={!interactive}
                    onClick={() =>
                      product.quantity > 1 &&
                      product.onQuantityChange?.(product.quantity - 1)
                    }
                    aria-label="Decrease quantity"
                  >
                    &minus;
                  </button>
                  <span className={styles.quantityValue}>
                    {product.quantity}
                  </span>
                  <button
                    type="button"
                    className={styles.quantityButton}
                    disabled={
                      !interactive ||
                      (product.maxQuantity !== undefined &&
                        product.quantity >= product.maxQuantity)
                    }
                    onClick={() =>
                      (product.maxQuantity === undefined ||
                        product.quantity < product.maxQuantity) &&
                      product.onQuantityChange?.(product.quantity + 1)
                    }
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        <div className={styles.pricingSection}>
          <div className={styles.pricingRow}>
            <span className={styles.pricingLabel}>{subtotalLabel}</span>
            <span className={styles.pricingValue}>{fmt(subtotal)}</span>
          </div>
          {discountAmount !== undefined && discountAmount > 0 && (
            <div className={styles.discountRow}>
              <span className={styles.pricingLabel}>{discountLabel}</span>
              <span>-{fmt(discountAmount)}</span>
            </div>
          )}
          <div className={styles.pricingRow}>
            <span className={styles.pricingLabel}>{shippingLabel}</span>
            <span className={styles.pricingValue}>
              {shippingCost ? fmt(shippingCost) : "Gratis"}
            </span>
          </div>
          {showTaxNotice && (
            <p className={styles.taxNotice}>* Impuestos Incluidos</p>
          )}
          <div className={styles.totalRow}>
            <span>{totalLabel}</span>
            <span>{fmt(total)}</span>
          </div>
          {taxEnabled && taxAmount > 0 && (
            <p className={styles.taxInfo}>
              Incluye {fmt(taxAmount)} en impuestos
              {taxName ? ` (${taxName})` : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
